/**
 * The live duel's HUD — clock, freeze countdown, both fighters' bars, and the
 * result card.
 *
 * One slice of the base-view stylesheet. `styles/index.ts` concatenates every
 * slice into the single sheet `injectStyles` mounts — the split is about
 * OWNERSHIP, not about emitting more than one style element.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below is
 * one template literal and a stray backtick terminates it. That has broken the
 * build twice, both times with esbuild pointing at a line ten rows away from
 * the cause. Write class names in plain text.
 */
export default `
/* ─── Duel HUD ──────────────────────────────────────────────────────────────
   This view has no panel registry and no other windows: the arena is its own
   mounted view, so nothing here has to dodge the settlement's scrims. Layers
   are local and start at 30. */
.bv-duel {
  position: absolute; inset: 0;
  pointer-events: none;
  z-index: 30;
  font-family: var(--font-technical, monospace);
  color: var(--bone, #e8ddc8);
}

/* Top bar: the two fighters either side of the clock. */
.bv-duel-top {
  position: absolute; top: 12px; left: 50%; transform: translateX(-50%);
  display: grid; grid-template-columns: 1fr auto 1fr; align-items: center;
  gap: 18px;
  width: min(680px, calc(100% - 24px));
  padding: 8px 14px;
  background: linear-gradient(180deg, rgba(24,20,17,0.92), rgba(16,13,11,0.88));
  border: 1px solid var(--brass, #8a7345);
  border-radius: var(--radius-sm, 3px);
  box-shadow: 0 6px 18px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05);
}

.bv-duel-fighter { min-width: 0; }
.bv-duel-fighter.is-them { text-align: right; }
.bv-duel-name {
  font-family: var(--font-display, sans-serif);
  font-size: 13px; letter-spacing: 0.06em; text-transform: uppercase;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  color: var(--bone, #e8ddc8);
}

/* The bar pair mirrors the in-world bars under each body, on purpose: the
   colours must agree or the HUD and the world read as two different systems. */
.bv-duel-track {
  position: relative; height: 7px; margin-top: 4px;
  background: rgba(27,22,19,0.9);
  border: 1px solid rgba(0,0,0,0.5);
  border-radius: 2px; overflow: hidden;
}
.bv-duel-track + .bv-duel-track { margin-top: 3px; }
.bv-duel-fill { position: absolute; inset: 0 auto 0 0; width: 0; transition: width 120ms linear; }
.bv-duel-fill.is-hp { background: linear-gradient(180deg, #d4523c, #8f3323); }
.bv-duel-fill.is-armor { background: linear-gradient(180deg, #9db2c4, #61788c); }
/* Mirrored so the opponent's bars drain toward the centre of the screen. */
.is-them .bv-duel-fill { inset: 0 0 0 auto; }

.bv-duel-clock {
  font-family: var(--font-display, sans-serif);
  font-size: 22px; letter-spacing: 0.08em;
  padding: 0 6px;
  color: var(--bone, #e8ddc8);
  text-shadow: 0 2px 0 rgba(0,0,0,0.6);
}
.bv-duel-clock.is-low { color: var(--warn, #e0a33c); }

/* The pre-duel freeze. Big, centred, and gone the moment it hits zero. */
.bv-duel-freeze {
  position: absolute; top: 34%; left: 50%; transform: translate(-50%, -50%);
  font-family: var(--font-display, sans-serif);
  font-size: clamp(64px, 14vw, 140px);
  line-height: 1;
  color: var(--bone, #e8ddc8);
  text-shadow: 0 6px 0 rgba(0,0,0,0.55), 0 0 40px rgba(0,0,0,0.6);
}
.bv-duel-freeze[hidden] { display: none; }
.bv-duel-freeze-label {
  display: block; text-align: center;
  font-size: 15px; letter-spacing: 0.3em; text-transform: uppercase;
  opacity: 0.75; margin-top: 10px;
}

/* Connection / seat failures. Never a silent hang — ADR-0001 is explicit that
   a WebSocket-hostile network needs a real error path. */
.bv-duel-error {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
  pointer-events: auto;
  max-width: min(420px, calc(100% - 32px));
  padding: 16px 18px; text-align: center;
  background: linear-gradient(180deg, rgba(30,18,16,0.96), rgba(20,12,11,0.96));
  border: 1px solid var(--rust, #8c4a35);
  border-radius: var(--radius-sm, 3px);
}
.bv-duel-error[hidden] { display: none; }

/* Result card — win / lose / draw. The first thing this game has ever told a
   player about the outcome of a duel.

   Janeks, 2026-10-16: "after fight (win / loose popups) also more game style
   ... some action and animation". A painted scene per outcome (Higgsfield,
   public/ui/arena/) on a slow drift, embers, turning rays on a win and a red
   pulse on a loss; the card lands with a jolt and the verdict stamps in one
   letter at a time. Transform and opacity only; the reduced-motion block at
   the end of this section lists every animation here. */
.bv-duel-result {
  position: absolute; inset: 0;
  pointer-events: auto; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  padding: max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom));
  background: #070504;
  animation: bv-duel-fade 0.35s ease-out both;
}
.bv-duel-result[hidden] { display: none; }
/* A decided duel owns the screen: lift the whole HUD over the hotbar (a
   sibling at z-index 32) so no weapon cell floats on top of the verdict. */
.bv-duel:has(> .bv-duel-result:not([hidden])) { z-index: 40; }
@keyframes bv-duel-fade { from { opacity: 0; } to { opacity: 1; } }
.bv-duel-result-art {
  position: absolute; inset: -5%;
  background: center 40% / cover no-repeat;
  animation: bv-duel-drift 24s ease-in-out infinite alternate;
  will-change: transform;
}
.bv-duel-result-art::after {
  content: ''; position: absolute; inset: 0;
  background:
    radial-gradient(ellipse 60% 55% at 50% 52%, rgba(7,5,4,0.15), rgba(7,5,4,0.85) 100%),
    linear-gradient(180deg, rgba(7,5,4,0.2), rgba(7,5,4,0) 35%, rgba(7,5,4,0.75));
}
.bv-duel-result.is-win .bv-duel-result-art { background-image: url('/ui/arena/victory.webp'); }
.bv-duel-result.is-lose .bv-duel-result-art { background-image: url('/ui/arena/defeat.webp'); }
.bv-duel-result.is-draw .bv-duel-result-art { background-image: url('/ui/arena/draw.webp'); }
@media (max-width: 720px) {
  .bv-duel-result.is-win .bv-duel-result-art { background-image: url('/ui/arena/victory-sm.webp'); }
  .bv-duel-result.is-lose .bv-duel-result-art { background-image: url('/ui/arena/defeat-sm.webp'); }
  .bv-duel-result.is-draw .bv-duel-result-art { background-image: url('/ui/arena/draw-sm.webp'); }
}
@keyframes bv-duel-drift {
  from { transform: scale(1.03) translate3d(-1%, 0.5%, 0); }
  to   { transform: scale(1.12) translate3d(1%, -1%, 0); }
}
/* A win: slow god-rays turning behind the card. */
.bv-duel-result-rays {
  position: absolute; left: 50%; top: 50%; width: 170vmax; height: 170vmax;
  margin: -85vmax 0 0 -85vmax; pointer-events: none; display: none;
  background: repeating-conic-gradient(from 0deg,
    rgba(255,200,110,0) 0deg, rgba(255,200,110,0.1) 5deg, rgba(255,200,110,0) 11deg, rgba(255,200,110,0) 20deg);
  mix-blend-mode: screen;
  -webkit-mask-image: radial-gradient(circle, #000 0, rgba(0,0,0,0.6) 25%, transparent 55%);
  mask-image: radial-gradient(circle, #000 0, rgba(0,0,0,0.6) 25%, transparent 55%);
  animation: bv-duel-turn 40s linear infinite;
}
.bv-duel-result.is-win .bv-duel-result-rays { display: block; }
@keyframes bv-duel-turn { to { transform: rotate(360deg); } }
/* A loss: the screen breathes red at the edges, like a pulse in the ears. */
.bv-duel-result.is-lose::after {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  box-shadow: inset 0 0 160px 40px rgba(150,20,10,0.75);
  animation: bv-duel-pulse 1.8s ease-in-out infinite;
}
@keyframes bv-duel-pulse { 0%, 100% { opacity: 0.45; } 50% { opacity: 1; } }
/* Embers fall cold on a loss and a draw: fewer, dimmer, ash not sparks. */
.bv-duel-result .bv-embers { z-index: 0; }
.bv-duel-result.is-lose .bv-embers i, .bv-duel-result.is-draw .bv-embers i {
  background: radial-gradient(circle, #c9c1b5 0, rgba(140,130,120,0.6) 45%, rgba(120,110,100,0) 72%);
}
.bv-duel-result.is-draw .bv-embers i:nth-child(odd) { display: none; }

.bv-duel-result-card {
  position: relative; z-index: 1;
  width: min(440px, 100%);
  padding: 30px 28px 24px; text-align: center;
  background:
    linear-gradient(180deg, rgba(26,19,12,0.82), rgba(10,7,5,0.92)),
    url('/clutter/plate.webp') 0 0 / 420px 420px, #17110a;
  border: 1px solid var(--brass, #8a7345);
  border-radius: var(--radius-panel, 4px);
  box-shadow: inset 0 1px 0 rgba(255,235,200,0.12), inset 0 0 0 1px rgba(0,0,0,0.55),
              0 0 50px rgba(0,0,0,0.6), 0 24px 60px rgba(0,0,0,0.7);
  animation: bv-duel-land 0.55s cubic-bezier(.2,1.2,.35,1) 0.15s both;
}
.bv-duel-result-card::before {
  content: ''; position: absolute; inset: 0; pointer-events: none; border-radius: inherit;
  background:
    url('/clutter/scratches.webp') center / 100% 100% no-repeat,
    url('/clutter/grime.webp') 60% 30% / 420px 420px;
  opacity: 0.35; mix-blend-mode: screen;
}
.bv-duel-result-card > * { position: relative; }
.bv-duel-result-card > .bv-duel-result-band {
  position: absolute; left: 0; right: 0; top: 0; height: 8px;
  background: repeating-linear-gradient(135deg, var(--brass-hot, #ffbd5c) 0 9px, #1a120a 9px 18px);
  opacity: 0.9;
}
.bv-duel-result.is-lose .bv-duel-result-band {
  background: repeating-linear-gradient(135deg, var(--rust, #b8412f) 0 9px, #1a0a06 9px 18px);
}
.bv-duel-result.is-draw .bv-duel-result-band {
  background: repeating-linear-gradient(135deg, #8d8577 0 9px, #15120e 9px 18px);
}
@keyframes bv-duel-land {
  0%   { transform: translate3d(0, -40px, 0) scale(1.08); opacity: 0; }
  55%  { transform: translate3d(0, 6px, 0) scale(0.98); opacity: 1; }
  75%  { transform: translate3d(-3px, -2px, 0); }
  100% { transform: none; opacity: 1; }
}
/* A loss lands harder: the whole card shakes once after it hits. */
.bv-duel-result.is-lose .bv-duel-result-card {
  animation: bv-duel-land 0.55s cubic-bezier(.2,1.2,.35,1) 0.15s both, bv-duel-shake 0.45s ease-out 0.7s;
}
@keyframes bv-duel-shake {
  0%, 100% { transform: none; }
  20% { transform: translate3d(-7px, 2px, 0) rotate(-0.6deg); }
  40% { transform: translate3d(6px, -2px, 0) rotate(0.5deg); }
  60% { transform: translate3d(-4px, 1px, 0); }
  80% { transform: translate3d(2px, 0, 0); }
}

.bv-duel-kicker {
  margin: 4px 0 2px;
  font-family: var(--font-display, sans-serif);
  font-size: 11px; letter-spacing: 0.26em; text-transform: uppercase;
  color: var(--bone-dim, #b9ab92);
  animation: bv-duel-rise 0.4s ease-out 0.45s both;
}
.bv-duel-verdict {
  font-family: var(--font-display, sans-serif);
  font-size: clamp(42px, 13vw, 64px); line-height: 1.05;
  letter-spacing: 0.12em; text-transform: uppercase;
  margin: 0 0 6px; white-space: nowrap;
}
.bv-duel-verdict span {
  display: inline-block;
  animation: bv-duel-stamp 0.42s cubic-bezier(.2,1.4,.4,1) both;
  animation-delay: calc(0.55s + var(--i, 0) * 0.06s);
}
@keyframes bv-duel-stamp {
  0%   { transform: scale(2.4) translate3d(0, -12px, 0); opacity: 0; }
  60%  { transform: scale(0.92); opacity: 1; }
  100% { transform: none; opacity: 1; }
}
.bv-duel-verdict.is-win {
  color: #ffd27a;
  text-shadow: 0 4px 0 #3a2208, 0 0 28px rgba(255,180,80,0.7), 0 0 60px rgba(255,140,40,0.35);
}
.bv-duel-verdict.is-lose {
  color: #e2553d;
  text-shadow: 0 4px 0 #2a0904, 0 0 26px rgba(200,40,20,0.6);
}
.bv-duel-verdict.is-draw {
  color: #cfc3ad;
  text-shadow: 0 4px 0 #1a150f, 0 0 20px rgba(200,190,170,0.3);
}
/* The rule under the verdict: two brass lines and the arena emblem. */
.bv-duel-rule {
  display: flex; align-items: center; gap: 10px; margin: 4px auto 10px; width: 78%;
  animation: bv-duel-rise 0.4s ease-out 1s both;
}
.bv-duel-rule::before, .bv-duel-rule::after {
  content: ''; flex: 1 1 auto; height: 1px;
  background: linear-gradient(90deg, rgba(210,164,86,0), var(--brass, #8a7345));
}
.bv-duel-rule::after { background: linear-gradient(270deg, rgba(210,164,86,0), var(--brass, #8a7345)); }
.bv-duel-rule img { width: 44px; height: 44px; filter: drop-shadow(0 3px 4px rgba(0,0,0,0.7)); }
.bv-duel-result.is-lose .bv-duel-rule img { filter: grayscale(0.7) brightness(0.7) drop-shadow(0 3px 4px rgba(0,0,0,0.7)); transform: rotate(14deg); }
.bv-duel-result.is-draw .bv-duel-rule img { filter: grayscale(1) brightness(0.8) drop-shadow(0 3px 4px rgba(0,0,0,0.7)); }
.bv-duel-sub {
  font-size: 13px; line-height: 1.5; opacity: 0.85; margin: 0 0 20px;
  animation: bv-duel-rise 0.4s ease-out 1.1s both;
}
.bv-duel-result-card .bv-duel-btn { animation: bv-duel-rise 0.4s ease-out 1.3s both; }
@keyframes bv-duel-rise { from { transform: translate3d(0, 10px, 0); opacity: 0; } to { transform: none; opacity: 1; } }

.bv-duel-btn {
  pointer-events: auto;
  min-height: 44px;
  font-family: var(--font-display, sans-serif);
  font-size: 13px; letter-spacing: 0.1em; text-transform: uppercase;
  padding: 10px 22px; cursor: pointer;
  color: var(--bone, #e8ddc8);
  background: linear-gradient(180deg, #4a3a2a, #241b13);
  border: 1px solid #0e0905;
  border-radius: var(--radius-well, 3px);
  box-shadow: inset 0 1px 0 rgba(255,235,200,0.14), inset 0 0 0 1px rgba(210,164,86,0.35),
              0 3px 0 #0a0603, 0 6px 10px rgba(0,0,0,0.5);
  transition: filter 0.12s ease, transform 0.12s ease;
}
.bv-duel-btn:hover { filter: brightness(1.18); }
.bv-duel-btn:active { transform: translateY(2px); }
.bv-duel-result.is-win .bv-duel-result-card .bv-duel-btn {
  color: #1a1206;
  background: linear-gradient(170deg, #ffd98e, var(--brass-hot, #ffbd5c) 50%, #a8742c);
  text-shadow: 0 1px 0 rgba(255,255,255,0.3);
}

@media (max-width: 480px) {
  .bv-duel-result-card { padding: 26px 16px 18px; }
  .bv-duel-verdict { letter-spacing: 0.08em; }
  .bv-duel-result-card .bv-duel-btn { width: 100%; }
}

@media (prefers-reduced-motion: reduce) {
  .bv-duel-result, .bv-duel-result-art, .bv-duel-result-rays, .bv-duel-result.is-lose::after,
  .bv-duel-result-card, .bv-duel-result.is-lose .bv-duel-result-card, .bv-duel-kicker,
  .bv-duel-verdict span, .bv-duel-rule, .bv-duel-sub, .bv-duel-result-card .bv-duel-btn {
    animation: none;
  }
  .bv-duel-result .bv-embers { display: none; }
  .bv-duel-btn { transition: none; }
}

/* Escape menu — forfeit is deliberately two clicks away. */
.bv-duel-menu {
  position: absolute; inset: 0;
  pointer-events: auto;
  display: flex; align-items: center; justify-content: center;
  background: rgba(8,6,5,0.6);
}
.bv-duel-menu[hidden] { display: none; }
.bv-duel-menu-card {
  display: flex; flex-direction: column; gap: 10px;
  padding: 22px 26px;
  background: linear-gradient(180deg, rgba(28,23,19,0.98), rgba(18,15,12,0.98));
  border: 1px solid var(--brass, #8a7345);
  border-radius: var(--radius-sm, 3px);
}
.bv-duel-menu-title {
  font-family: var(--font-display, sans-serif);
  font-size: 15px; letter-spacing: 0.1em; text-transform: uppercase;
  text-align: center; margin: 0 0 4px;
}
.bv-duel-btn.is-danger { border-color: var(--rust, #8c4a35); color: #e8c4bb; }

/* Your own shot: a short blur at the edges of the screen, clear in the middle
   (render/shot-feedback.ts). Above the canvas, under every control, and never
   takes a click. Paired with a render-only view-offset shake. */
.bv-shot-blur {
  position: absolute; inset: 0; z-index: 1; pointer-events: none; opacity: 0;
  -webkit-backdrop-filter: blur(3px); backdrop-filter: blur(3px);
  -webkit-mask-image: radial-gradient(ellipse at center, transparent 45%, #000 100%);
  mask-image: radial-gradient(ellipse at center, transparent 45%, #000 100%);
}
.bv-shot-blur.bv-shot-blur-on { animation: bv-shot-blur 260ms ease-out; }
@keyframes bv-shot-blur { 0% { opacity: 1; } 100% { opacity: 0; } }
@media (prefers-reduced-motion: reduce) { .bv-shot-blur { display: none; } }
`;
